import FW from '../../fw/fw';
import Logger from '../../fw/logger';
import StreamingHelpers from '../streaming-helpers';
import DvrManagement from '../dvr';
import CCManagement from '../../accessibility/cc';
import UIManagement from '../../ui/ui';
import ConfigureShakaPlayer from './config';
import AWSMediaTailor from './aws-media-tailor';


export default class ShakaPlayer {

  #rmp;
  #shakaVideoRenditions = [];
  #shakaFramerateIntervalFn = null;
  #shakaOfflineDownloadProgress = -1;
  #shakaErrorFn = null;
  #shakaAdaptationChangedFn = null;
  #shakaTracksChangedFn = null;
  #shakaManifestParsedFn = null;
  #shakaDurationChangeFn = null;
  #shakaEmsgFn = null;
  #shakaBufferingFn = null;
  #shakaMetadataFn = null;
  #hasThumbnail = false;
  #previousVariantTracks = undefined;
  #shakaP2PEngine = null;

  constructor(rmp) {
    this.#rmp = rmp;
    this.#rmp.rmpStreamingHelpers = new StreamingHelpers(rmp);
  }

  get hasThumbnailTrack() {
    return this.#hasThumbnail;
  }

  get shakaP2PEngine() {
    return this.#shakaP2PEngine;
  }

  get shakaOfflineDownloadProgress() {
    return this.#shakaOfflineDownloadProgress;
  }

  set shakaOfflineDownloadProgress(progress) {
    this.#shakaOfflineDownloadProgress = progress;
  }

  get shakaVideoRenditions() {
    return this.#shakaVideoRenditions;
  }

  static #filterVideoVariants(variantTracks) {
    let filteredVariantTracks = variantTracks;
    filteredVariantTracks.sort((a, b) => {
      return a.height - b.height;
    });
    filteredVariantTracks.sort((a, b) => {
      return b.audioBandwidth - a.audioBandwidth;
    });
    // If there is a selected variant track, then we filter out any tracks in
    // a different language.  Then we use those remaining tracks to display the
    // available resolutions.
    const selectedTrack = filteredVariantTracks.find(track => {
      return track.active;
    });
    if (selectedTrack) {
      // Filter by current audio language and audioId.
      filteredVariantTracks = filteredVariantTracks.filter(track => {
        return track.language === selectedTrack.language && track.audioId === selectedTrack.audioId;
      });
    }
    return filteredVariantTracks;
  }

  static #returnAverageVariant(variantTracks) {
    let variant;
    const length = variantTracks.length;
    if (length === 1) {
      variant = variantTracks[0];
    } else {
      variant = variantTracks[Math.floor(length / 2)];
    }
    return variant;
  }

  #updateUIAbrActiveTrack() {
    const variantTracks = this.#rmp.shakaPlayer.getVariantTracks();
    if (variantTracks.length > 0 && this.#rmp.dom.qualityOverlayLevelsArea) {
      const qualities = this.#rmp.dom.qualityOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
      if (qualities.length > 0) {
        for (let i = 0, len = qualities.length; i < len; i++) {
          qualities[i].classList.remove('rmp-abr-active');
        }
        const filteredVariantTracks = ShakaPlayer.#filterVideoVariants(variantTracks);
        for (let i = 0, len = filteredVariantTracks.length; i < len; i++) {
          const variant = filteredVariantTracks[i];
          if (variant.active) {
            for (let i = 0, len = qualities.length; i < len; i++) {
              const trackDom = qualities[i];
              const dataId = trackDom.getAttribute('data-track-id');
              if (dataId) {
                if (parseInt(dataId) === variant.id) {
                  trackDom.classList.add('rmp-abr-active');
                  this.#rmp.rmpStreamingHelpers.updateQualityModuleIcon(trackDom);
                  break;
                }
              }
            }
            break;
          }
        }
      }
    }
  }

  #setUpAudioTracks(audioTracks) {
    Logger.print('shaka-player', 'setting up audio tracks UI');
    this.#rmp.rmpModuleManagement.appendToControlBar('audio');
    this.#rmp.rmpModuleManagement.appendOverlay('audio');
    audioTracks.forEach(audioTrack => {
      let className;
      if (audioTrack.active) {
        className = 'rmp-button rmp-overlay-level rmp-color-bg rmp-overlay-level-active';
      } else {
        className = 'rmp-button rmp-overlay-level rmp-color-bg';
      }
      let audioTrackText = 'Audio ' + audioTrack.id;
      if (this.#rmp.customTrackNames.audio.length > 0 && this.#rmp.customTrackNames.audio[audioTrack.id]) {
        audioTrackText = this.#rmp.customTrackNames.audio[audioTrack.id];
      } else {
        let label;
        if (audioTrack.label) {
          audioTrackText = label = audioTrack.label;
        }
        if (!label) {
          audioTrackText = FW.mapLanguageCodeToName(audioTrack.language);
        }
        if (audioTrack.accessibility) {
          audioTrackText += ' - ' + audioTrack.accessibility;
        }
        if (audioTrack.channelsCount > 2) {
          audioTrackText += ' - surround';
        }
      }
      const langDiv = FW.createElement(
        'button',
        className,
        audioTrackText,
        this.#rmp.rtl,
        true
      );
      this.#rmp.rmpAccessible.button(langDiv, audioTrackText, true);
      const selectAudioTrackFn = this.#rmp.rmpStreamingHelpers.selectAudioTrack.bind(this.#rmp.rmpStreamingHelpers, audioTrack.id);
      FW.addEvents(['touchend', 'click'], langDiv, selectAudioTrackFn);
      this.#rmp.dom.audioOverlayLevelsArea.appendChild(langDiv);
    });
  }

  #setUpTextTracks() {
    this.#rmp.textTracks = StreamingHelpers.filterNativeTextTracks(this.#rmp.shakaPlayer.getTextTracks());
    if (this.#rmp.textTracks.length > 0) {
      Logger.print('shaka-player', 'filtered in-manifest/in-band text tracks follow', this.#rmp.textTracks);
      if (this.#rmp.autoBumpCaptionsOnVisibleControlBar) {
        UIManagement.appendCSSForNativeCCRendering();
      }
      this.#rmp.textTracks.forEach((textTrack, index) => {
        this.#rmp.loadedCCFiles[index] = {};
        let lng = textTrack.language || textTrack.lang;
        if (!lng) {
          lng = this.#rmp.labels.hint.track + ' ' + (index + 1);
        }
        this.#rmp.loadedCCFiles[index].lng = lng;
        let label = textTrack.label || textTrack.name;
        if (!label) {
          label = FW.mapLanguageCodeToName(textTrack.language || textTrack.lang);
          if (!label) {
            label = lng;
          }
        }
        if (this.#rmp.customTrackNames.captions.length > 0 && this.#rmp.customTrackNames.captions[index]) {
          label = this.#rmp.customTrackNames.captions[index];
        }
        this.#rmp.loadedCCFiles[index].name = label;
        this.#rmp.loadedCCFiles[index].uri = textTrack.src || '';
        this.#rmp.loadedCCFiles[index].data = null;
        // update UI for default MPEG-DASH Text Track
        if (textTrack.active) {
          this.#rmp.loadedCCFiles[index].default = true;
        }
      });
      this.#rmp.rmpCCManagement = new CCManagement(this.#rmp);
      this.#rmp.rmpCCManagement.appendUI();
    }
  }

  static #compareVariantTracks(previousVariantTracks, newVariantTracks) {
    if (previousVariantTracks.length === newVariantTracks.length) {
      const previousIdsArray = [];
      previousVariantTracks.forEach(variant => {
        previousIdsArray.push(variant.id);
      });

      previousIdsArray.sort((a, b) => {
        return a - b;
      });


      const newIdsArray = [];
      newVariantTracks.forEach(variant => {
        newIdsArray.push(variant.id);
      });
      newIdsArray.sort((a, b) => {
        return a - b;
      });

      for (let i = 0; i < previousIdsArray.length; ++i) {
        if (previousIdsArray[i] !== newIdsArray[i]) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  // multi-period where tracks are likely to change
  #shakaTracksChanged() {
    Logger.print('shaka-player', 'TracksChangedEvent - updating UI');

    const newVariantTracks = this.#rmp.shakaPlayer.getVariantTracks();

    // we need to check this as TracksChangedEvent now fires when changing text track in Shaka 4.12+ :(-
    if (this.#previousVariantTracks !== undefined) {
      const same = ShakaPlayer.#compareVariantTracks(this.#previousVariantTracks, newVariantTracks);
      if (same) {
        Logger.print('shaka-player', 'TracksChangedEvent fires but VariantTracks are the same - no op');
        return;
      }
    }

    Logger.print('shaka-player', 'removing previous quality/audio/captions UI');
    if (this.#rmp.dom.quality) {
      this.#rmp.rmpModuleManagement.destroy('quality');
    }
    if (this.#rmp.dom.audio) {
      this.#rmp.rmpModuleManagement.destroy('audio');
    }
    if (this.#rmp.dom.captions && this.#rmp.rmpCCManagement && !this.#rmp.hasExternalCCFiles) {
      this.#rmp.rmpCCManagement.destroy();
    }


    this.#previousVariantTracks = newVariantTracks;
    Logger.print('shaka-player', 'new variants tracks', newVariantTracks);

    const textTracks = this.#rmp.shakaPlayer.getTextTracks();
    Logger.print('shaka-player', 'text tracks follow', textTracks);

    // call audioTracks getter before setUpVideoRenditions
    const audioTracks = this.#rmp.audioTracks;

    // set ABR UI and video renditions in quality menu
    this.#rmp.rmpStreamingHelpers.setUpAbrUI();
    this.setUpVideoRenditions();

    if (!this.#rmp.audioOnly) {
      if (audioTracks.length > 1) {
        this.#setUpAudioTracks(audioTracks);
      }
      // captions tracks for audio/video feeds
      if (!this.#rmp.hasExternalCCFiles && textTracks.length > 0) {
        this.#setUpTextTracks();
      }
    }
  }

  #shakaAdaptationChanged() {
    const configuration = this.#rmp.shakaPlayer.getConfiguration();
    if (configuration && configuration.abr && configuration.abr.enabled) {
      this.#updateUIAbrActiveTrack();
    }
    this.#rmp.rmpUtils.fireLevelSwitchEvents();
  }

  #shakaDurationChange() {
    if (this.#rmp.detectLive) {
      const isLive = this.#rmp.shakaPlayer.isLive();
      Logger.print('shaka-player', 'stream is a live stream: ' + isLive);
      if (isLive) {
        const seekRange = this.#rmp.shakaPlayer.seekRange();
        const diff = seekRange.end - seekRange.start;
        if (diff > this.#rmp.dvrUIThreshold) {
          if (!this.#rmp.rmpDvrManagement) {
            this.#rmp.rmpDvrManagement = new DvrManagement(this.#rmp);
          }
          this.#rmp.rmpDvrManagement.appendDvrUI();
        } else {
          this.#rmp.rmpUIManagement.prepareLiveUI();
        }
      } else {
        this.#rmp.rmpSeekManagement.appendSeekBar();
      }
      this.#rmp.detectLive = false;
    }
  }

  #shakaManifestParsed() {
    this.#rmp.dispatch('manifestparsed');
  }

  #offlineSelectTracks(tracks) {
    // input tracks do not show active
    // shakaPlayer.getVariantTracks() do show active but for fmp4-HLS there could be some mismatch ID 
    // so we guess active to get language data from shakaPlayer.getVariantTracks()
    // but we use tracks to select track
    let variant;
    const inputVariantTracks = tracks.filter(track => {
      return track.type === 'variant';
    });
    Logger.print('offline', 'available variant tracks for offline storage follow', inputVariantTracks);
    if (inputVariantTracks.length === 0) {
      return [tracks[0]];
    }
    const variantTracks = this.#rmp.shakaPlayer.getVariantTracks();
    const activeVariant = variantTracks.find(track => {
      return track.active;
    });
    if (!activeVariant) {
      return [ShakaPlayer.#returnAverageVariant(inputVariantTracks)];
    }
    const lngActiveVariant = activeVariant.language;
    const variantLngTracks = inputVariantTracks.filter(track => {
      return track.language === lngActiveVariant;
    }).sort((a, b) => {
      return a.bandwidth > b.bandwidth;
    });
    if (variantLngTracks.length === 0) {
      return [ShakaPlayer.#returnAverageVariant(inputVariantTracks)];
    }
    Logger.print('offline', 'filtered variant tracks for offline storage follow', variantLngTracks);
    // max -3
    // min -2
    // average sup -1
    switch (this.#rmp.shakaOfflinePreferredTrackQuality) {
      case -3:
        Logger.print('offline', 'selecting the highest quality for offline storage');
        variant = variantLngTracks[variantLngTracks.length - 1];
        break;
      case -2:
        Logger.print('offline', 'selecting the lowest quality for offline storage');
        variant = variantLngTracks[0];
        break;
      case -1:
        Logger.print('offline', 'selecting the upper average quality for offline storage');
        variant = ShakaPlayer.#returnAverageVariant(variantLngTracks);
        break;
      default:
        if (this.#rmp.shakaOfflinePreferredTrackQuality >= 0 && variantLngTracks[this.#rmp.shakaOfflinePreferredTrackQuality]) {
          Logger.print('offline', 'selecting quality ' + this.#rmp.shakaOfflinePreferredTrackQuality + ' for offline storage');
          variant = variantLngTracks[this.#rmp.shakaOfflinePreferredTrackQuality];
        } else {
          variant = ShakaPlayer.#returnAverageVariant(variantLngTracks);
        }
        break;
    }
    Logger.print('offline', 'selected variant for offline storage is', variant);
    return [variant];
  }

  #shakaOfflineInit() {
    Logger.print('offline', 'offline storage is supported and requested - init');
    window.rmpGlobals.rmpStorage = new window.shaka.offline.Storage(this.#rmp.shakaPlayer);
    const configureStorage = {
      offline: {
        progressCallback: this.offlineDownloadProgress.bind(this)
      }
    };
    if (this.#rmp.shakaOfflinePreferredTrackQuality !== -4) {
      configureStorage.offline.trackSelectionCallback = this.#offlineSelectTracks.bind(this);
    }
    this.#rmp.shakaPlayer.configure(configureStorage);
  }

  #shakaBuffering(event) {
    // sometimes HTML5 video waiting event does not fire but buffer is empty
    // example slow 3G in Chrome dev tools with MPEG-DASH 1 1080p rendition
    // we need to force waiting event for buffer API to work
    if (event && typeof event.buffering !== 'undefined') {
      Logger.print('shaka-player', 'internal buffering event - buffering is now ' + event.buffering);
      if (event.buffering) {
        this.#rmp.rmpEventsManagement.waitingCallback();
      } else {
        this.#rmp.rmpEventsManagement.unstalledCallback();
      }
    }
  }

  #shakaEmsg(event) {
    if (event && event.type && event.detail) {
      this.#rmp.meta = {};
      this.#rmp.meta.type = event.type;
      this.#rmp.meta.data = event.detail;
      this.#rmp.dispatch('meta');
    }
  }

  #shakaMetadata(event) {
    if (event && event.type && event.payload) {
      this.#rmp.meta = {};
      this.#rmp.meta.type = 'id3';
      this.#rmp.meta.data = event.payload.data;
      this.#rmp.dispatch('meta');
    }
  }

  async #createShakaInstance() {
    this.#rmp.shakaPlayer = new window.shaka.Player();
    try {
      await this.#rmp.shakaPlayer.attach(this.#rmp.dom.video);
    } catch (e) {
      this.shakaError(e);
    }
    // bitmovin analytics
    if (window.bitmovin && this.#rmp.bitmovinAnalyticsConfig.key && typeof this.#rmp.bitmovinAnalyticsConfig.key === 'string') {
      Logger.print('bitmovin analytics', 'adding Shaka Player analytics support', this.#rmp.bitmovinAnalyticsConfig);
      this.#rmp.bitmovinAnalytics = new window.bitmovin.analytics.adapters.ShakaAdapter(
        this.#rmp.bitmovinAnalyticsConfig,
        this.#rmp.shakaPlayer
      );
      Logger.print('bitmovin analytics', 'bitmovinAnalytics', this.#rmp.bitmovinAnalytics);
    }

    // configure
    this.#rmp.rmpConfigureShakaPlayer = new ConfigureShakaPlayer(this.#rmp);
    this.#rmp.rmpConfigureShakaPlayer.init();

    // shakaPlayer events
    this.#shakaErrorFn = this.shakaError.bind(this);
    this.#rmp.shakaPlayer.addEventListener('error', this.#shakaErrorFn);

    this.#shakaAdaptationChangedFn = this.#shakaAdaptationChanged.bind(this);
    this.#rmp.shakaPlayer.addEventListener('adaptation', this.#shakaAdaptationChangedFn);

    this.#shakaTracksChangedFn = this.#shakaTracksChanged.bind(this);
    this.#rmp.shakaPlayer.addEventListener('trackschanged', this.#shakaTracksChangedFn);

    this.#shakaManifestParsedFn = this.#shakaManifestParsed.bind(this);
    this.#rmp.shakaPlayer.addEventListener('manifestparsed', this.#shakaManifestParsedFn);

    this.#shakaEmsgFn = this.#shakaEmsg.bind(this);
    this.#rmp.shakaPlayer.addEventListener('emsg', this.#shakaEmsgFn);

    this.#shakaMetadataFn = this.#shakaMetadata.bind(this);
    this.#rmp.shakaPlayer.addEventListener('metadata', this.#shakaMetadataFn);

    this.#shakaBufferingFn = this.#shakaBuffering.bind(this);
    this.#rmp.shakaPlayer.addEventListener('buffering', this.#shakaBufferingFn);

    // HTML5 video events
    this.#shakaDurationChangeFn = this.#shakaDurationChange.bind(this);
    this.#rmp.dom.video.addEventListener('durationchange', this.#shakaDurationChangeFn);

    this.#rmp.dom.video.addEventListener('playing', () => {
      const imageTracks = this.#rmp.shakaPlayer.getImageTracks();
      Logger.print('shaka-player', 'imageTracks follows', imageTracks);
      if (imageTracks && imageTracks.length > 0) {
        this.#hasThumbnail = true;
      }
    }, { once: true });


    if (this.#rmp.shakaOffline && this.#rmp.isVod) {
      if (!this.#rmp.environment.hasDownloadSupport) {
        this.#rmp.rmpUtils.warning(3007);
      } else {
        this.#shakaOfflineInit();
      }
    }
    Logger.print('shaka-player', 'trying to load manifest at ' + this.#rmp.startingSrcUri);

    // Shaka player MUX 
    if (this.#rmp.rmpMux) {
      this.#rmp.rmpMux.initShakaPlayer();
    }

    if (window.shaka.log && window.shaka.log.setLevel) {
      window.shaka.log.setLevel(window.shaka.log.Level.ERROR);
      // log level
      if (DEBUG) {
        window.shaka.log.setLevel(window.shaka.log.Level.INFO);
        if (this.#rmp.debugLevel === 2) {
          window.shaka.log.setLevel(window.shaka.log.Level.V1);
        } else if (this.#rmp.debugLevel === 3) {
          window.shaka.log.setLevel(window.shaka.log.Level.V2);
        }
      }
    }

    if (this.#rmp.shakaAWSMediaTailor) {
      this.#rmp.rmpShakaAWSMediaTailor = new AWSMediaTailor(this.#rmp);
      this.#rmp.rmpShakaAWSMediaTailor.load();
    } else {
      this.loadOrPreload(this.#rmp.startingSrcUri);
    }
    this.#rmp.shakaInstanceCreated = true;
    this.#rmp.dispatch('shakainstancecreated');
  }

  #initShaka() {
    window.shaka.polyfill.installAll();
    if (window.shaka.Player.isBrowserSupported()) {
      this.#createShakaInstance();
    } else {
      this.#rmp.rmpUtils.error(302);
    }
  }

  #loadShaka() {
    if (typeof window.shaka !== 'undefined') {
      this.#initShaka();
    } else {
      // do not change to const pathToRmpFiles = this.#rmp.pathToRmpFiles or grunt replace won't work
      let callScript = this.#rmp.pathToRmpFiles + 'dash/shaka-player.ui.js';
      if (DEBUG) {
        callScript = this.#rmp.pathToRmpFiles + 'dash/shaka-player.ui.debug.js';
      }
      Logger.print('shaka-player', 'shaka-player library starts loading at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
      FW.getScript(
        callScript,
        this.#initShaka.bind(this),
        this.#rmp.rmpUtils.error.bind(this.#rmp.rmpUtils, 300),
        this.#rmp.shadowRoot
      );
    }
  }

  #getFrameRate() {
    const variantTracks = this.#rmp.shakaPlayer.getVariantTracks();
    const activeTrack = variantTracks.find(track => {
      return track.active;
    });
    if (activeTrack && typeof activeTrack.frameRate === 'number' && activeTrack.frameRate > 0) {
      if (this.#rmp.frameRate !== activeTrack.frameRate) {
        this.#rmp.frameRate = activeTrack.frameRate;
        Logger.print('hls.js', 'updated framerate is: ' + this.#rmp.frameRate);
        this.#rmp.dispatch('framerateavailable');
      }
    }
  }

  async loadOrPreload(uri) {
    try {
      if (this.#rmp.p2pMediaLoaderEngine !== null) {
        Logger.print('shaka-player', 'p2pMediaLoader enabled', this.#rmp.p2pMediaLoaderEngine);
        this.#rmp.p2pMediaLoaderEngine.registerPlugins();
        // Initialize P2P Media Loader with custom config
        this.#shakaP2PEngine = new this.#rmp.p2pMediaLoaderEngine(this.#rmp.p2pMediaLoaderConfig);
        this.#rmp.dispatch('shakap2pengineavailable', this.#shakaP2PEngine);
        // Configure and initialize Shaka Player with P2P Media Loader
        this.#shakaP2PEngine.bindShakaPlayer(this.#rmp.shakaPlayer);
      }
      // if player already initialized (early click) -> not need to preload
      if (this.#rmp.preload === 'metadata' && !this.#rmp.rmpPlaybackManagement.playerInitialized) {
        this.#rmp.shakaPreloadManager = await this.#rmp.shakaPlayer.preload(uri);
        if (this.#rmp.shakaPreloadManager) {
          Logger.print('shaka-player', 'preloading manifest and first few segments');
        } else {
          await this.#rmp.shakaPlayer.load(uri);
        }
      } else {
        await this.#rmp.shakaPlayer.load(uri);
      }
    } catch (error) {
      this.shakaError(error);
    }
  }

  shakaError(e) {
    if (e) {
      if (this.#rmp.rmpMux && this.#rmp.rmpMux.shakaPlayer) {
        this.#rmp.rmpMux.shakaPlayer.loadErrorHandler(e);
      }
      let event = e;
      if (e && e.detail) {
        event = e.detail;
      }
      if (typeof event.severity === 'number' && event.severity === 2 && typeof event.category === 'number') {
        const category = event.category;
        let errorCode = 301;
        switch (category) {
          case 1:
            errorCode = 303;
            break;
          case 2:
            errorCode = 304;
            break;
          case 3:
            errorCode = 305;
            break;
          case 4:
            errorCode = 306;
            break;
          case 5:
            errorCode = 307;
            break;
          case 6:
            errorCode = 308;
            break;
          case 9:
            errorCode = 309;
            break;
          default:
            break;
        }
        this.#rmp.rmpUtils.error(errorCode, event);
      } else {
        this.#rmp.rmpUtils.warning(3000, event);
      }
    }
  }

  offlineDownloadProgress(content, progress) {
    this.#shakaOfflineDownloadProgress = progress;
    this.#rmp.dispatch('downloadprogress', progress);
  }

  init() {
    if (this.#rmp.msePreloadRequested) {
      this.#rmp.msePreloadRequested = false;
      return;
    }
    this.#loadShaka();
  }

  destroy() {
    if (this.#rmp.shakaPlayer) {
      this.#rmp.shakaPlayer.removeEventListener('error', this.#shakaErrorFn);
      this.#rmp.shakaPlayer.removeEventListener('adaptation', this.#shakaAdaptationChangedFn);
      this.#rmp.shakaPlayer.removeEventListener('trackschanged', this.#shakaTracksChangedFn);
      this.#rmp.shakaPlayer.removeEventListener('manifestparsed', this.#shakaManifestParsedFn);
      this.#rmp.shakaPlayer.removeEventListener('emsg', this.#shakaEmsgFn);
      this.#rmp.shakaPlayer.removeEventListener('metadata', this.#shakaMetadataFn);
      this.#rmp.shakaPlayer.removeEventListener('buffering', this.#shakaBufferingFn);
    }
    if (this.#rmp.dom.video) {
      this.#rmp.dom.video.removeEventListener('durationchange', this.#shakaDurationChangeFn);
    }
    window.clearInterval(this.#shakaFramerateIntervalFn);
    this.#shakaOfflineDownloadProgress = -1;
    this.#shakaErrorFn = null;
    this.#shakaAdaptationChangedFn = null;
    this.#shakaTracksChangedFn = null;
    this.#shakaManifestParsedFn = null;
    this.#shakaDurationChangeFn = null;
    this.#hasThumbnail = false;
  }

  setUpVideoRenditions() {
    this.#shakaVideoRenditions = [];

    const variantTracks = this.#rmp.shakaPlayer.getVariantTracks();
    const availableVideoVariants = ShakaPlayer.#filterVideoVariants(variantTracks);
    Logger.print('shaka-player', 'availableVideoVariants follow', availableVideoVariants);

    // framerate handling
    // in case frameRate is set with input setting we already have frameRate
    if (this.#rmp.frameRate === -1) {
      this.#getFrameRate();
      this.#shakaFramerateIntervalFn = window.setInterval(() => {
        this.#getFrameRate();
      }, 16.67);
    }

    // run through video rendition and update UI
    const trackLabel = this.#rmp.labels.hint.track;
    availableVideoVariants.forEach((videoTrack, index) => {
      const newStream = FW.createElement(
        'button',
        'rmp-button rmp-overlay-level rmp-color-bg rmp-q' + (index + 1),
        null,
        this.#rmp.rtl
      );
      let accessibleLabel = trackLabel + ' ' + (index + 1);
      newStream.setAttribute('data-track-id', videoTrack.id);
      this.#shakaVideoRenditions.push(videoTrack.id);
      let label = false;
      const height = videoTrack.height;
      if (typeof height === 'number' && height > 0) {
        label = true;
        accessibleLabel = height.toString() + 'p';
        newStream.appendChild(StreamingHelpers.getLevelHeight(videoTrack));
      }
      if (typeof videoTrack.bandwidth === 'number' && videoTrack.bandwidth > 10 &&
        (this.#rmp.bitrateDataDisplayed === 'kbps' || this.#rmp.bitrateDataDisplayed === 'mbps')) {
        label = true;
        if (height) {
          const span = document.createElement('span');
          FW.setText(span, ' @ ');
          newStream.appendChild(span);
          newStream.appendChild(StreamingHelpers.getBandwidth(videoTrack.bandwidth, this.#rmp.bitrateDataDisplayed));
        } else {
          newStream.appendChild(StreamingHelpers.getBandwidth(videoTrack.bandwidth, this.#rmp.bitrateDataDisplayed));
        }
      }
      if (videoTrack.videoCodec && this.#rmp.displayCodecData) {
        const span = document.createElement('span');
        StreamingHelpers.getCodecLabel(videoTrack, span);
        newStream.appendChild(span);
      }
      if (videoTrack.hdr && /hdr/i.test(videoTrack.hdr)) {
        const span = document.createElement('span');
        FW.setText(span, ` | ${videoTrack.hdr}`);
        newStream.appendChild(span);
      }
      if (this.#rmp.customTrackNames.quality.length > 0 && this.#rmp.customTrackNames.quality[index]) {
        accessibleLabel = this.#rmp.customTrackNames.quality[index];
        FW.setText(newStream, this.#rmp.customTrackNames.quality[index]);
      } else if (!label) {
        FW.setText(newStream, trackLabel + ' ' + (index + 1));
      }
      this.#rmp.rmpAccessible.button(newStream, accessibleLabel, true);
      this.#rmp.dom.qualityOverlayLevelsArea.appendChild(newStream);
    });
    if (this.#rmp.dom.qualityOverlayLevelsArea) {
      const qualities = this.#rmp.dom.qualityOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
      if (qualities.length > 0) {
        for (let i = 0, len = qualities.length; i < len; i++) {
          const selectRenditionFn = this.#rmp.rmpStreamingHelpers.selectRendition.bind(this.#rmp.rmpStreamingHelpers, i);
          FW.addEvents(['touchend', 'click'], qualities[i], selectRenditionFn);
        }
      }
    }
    this.#updateUIAbrActiveTrack();
  }

}
